import { useEffect, useState, useRef, useMemo } from "react";
import "./App.css";

// Hard safety net: reveal the hero iframe after this many ms regardless of
// whether Vimeo's postMessage('play') fires. The poster image is showing
// the entire time, so the user just transitions from poster to (possibly
// loading) video — never from a blank/black state.
const HERO_VIDEO_REVEAL_TIMEOUT_MS = 15000;

function App() {
  const [html, setHtml] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    window.toggleSample = function (btn) {
      const card = btn && btn.closest && btn.closest(".sample-card");
      if (card) card.classList.toggle("expanded");
    };
    fetch("/portfolio-base.html")
      .then((r) => r.text())
      .then((text) => {
        const bodyMatch = text.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        const styleMatch = text.match(/<style[\s\S]*?<\/style>/i);
        const fontLink = text.match(/<link[^>]+fonts\.googleapis[^>]*>/gi)?.join("\n") || "";
        const head = (fontLink ? fontLink : "") + (styleMatch ? styleMatch[0] : "");
        if (!document.getElementById("portfolio-injected-style")) {
          const wrap = document.createElement("div");
          wrap.id = "portfolio-injected-style";
          wrap.innerHTML = head;
          document.head.appendChild(wrap);
        }
        setHtml(bodyMatch ? bodyMatch[1] : text);
      });
  }, []);

  useEffect(() => {
    if (!html || !containerRef.current) return;
    const root = containerRef.current;

    // Smooth-scroll for any #contact anchor (modal removed — inline form lives there)
    const contactAnchors = root.querySelectorAll(
      'a[href="#contact"], a[href*="#contact"], .nav-cta'
    );
    contactAnchors.forEach((el) => {
      el.setAttribute("data-testid", "scroll-to-contact");
      el.addEventListener("click", (e) => {
        const target = document.getElementById("contact");
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    // mailto: links — native behavior (open user's mail client). Just tag for tests.
    root.querySelectorAll('a[href^="mailto:"]').forEach((el) => {
      el.setAttribute("data-testid", "mailto-link");
    });

    // Hero video: fade in only when Vimeo actually starts playing. The poster
    // image (set via CSS background) stays visible during the entire load,
    // including the gap between iframe-loaded and first-video-frame-painted.
    const heroWrap = root.querySelector(".hero-video-wrap");
    const heroIframe = heroWrap?.querySelector("iframe");
    if (heroWrap && heroIframe && !heroIframe.dataset.bound) {
      heroIframe.dataset.bound = "1";
      const reveal = () => heroWrap.classList.add("video-loaded");

      // Subscribe to Vimeo player events via postMessage. We ask for the
      // 'play' event so we only reveal once the first frame is visible.
      const subscribe = () => {
        try {
          heroIframe.contentWindow?.postMessage(
            JSON.stringify({ method: "addEventListener", value: "play" }),
            "*"
          );
        } catch (_) { /* same-origin restrictions, ignore */ }
      };

      const onMessage = (e) => {
        if (typeof e.data !== "string" || !e.data.includes("vimeo")) return;
        try {
          const data = JSON.parse(e.data);
          if (data && (data.event === "play" || data.event === "playProgress")) {
            reveal();
            window.removeEventListener("message", onMessage);
          }
        } catch (_) { /* not a JSON payload from Vimeo — ignore */ }
      };
      window.addEventListener("message", onMessage);

      heroIframe.addEventListener("load", subscribe, { once: true });
      if (heroIframe.contentDocument?.readyState === "complete") subscribe();

      // Hard safety net: reveal after the timeout no matter what. The user
      // has the poster showing all along; the worst case is they briefly see
      // a black flash before video kicks in.
      setTimeout(reveal, HERO_VIDEO_REVEAL_TIMEOUT_MS);
    }

    // Contact form: post to Google Forms via no-cors fetch so users stay on the site
    const form = root.querySelector("form.jw-form");
    if (form && !form.dataset.bound) {
      form.dataset.bound = "1";
      const action = form.getAttribute("data-gf-action");
      const successEl = form.querySelector(".jw-form-success");
      const errorEl = form.querySelector(".jw-form-error");
      const submitBtn = form.querySelector(".jw-submit");
      const submitLabel = form.querySelector(".jw-submit-label");
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        if (errorEl) errorEl.hidden = true;
        if (successEl) successEl.hidden = true;
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }
        const fd = new FormData(form);
        if (submitBtn) {
          submitBtn.disabled = true;
          if (submitLabel) submitLabel.textContent = "Sending…";
        }
        try {
          await fetch(action, {
            method: "POST",
            mode: "no-cors",
            body: fd,
          });
          form.reset();
          if (successEl) successEl.hidden = false;
          successEl?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        } catch (err) {
          if (errorEl) errorEl.hidden = false;
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            if (submitLabel) submitLabel.textContent = "Send Message";
          }
        }
      });
    }

    // Headshot height syncing
    const syncHeadshot = () => {
      const textCol = root.querySelector(".about-text-col");
      const inner = root.querySelector(".about-inner");
      if (!textCol || !inner) return;
      const h = textCol.getBoundingClientRect().height;
      inner.style.setProperty("--headshot-max-h", `${Math.round(h)}px`);
    };
    syncHeadshot();
    const ro = new ResizeObserver(syncHeadshot);
    const textCol = root.querySelector(".about-text-col");
    if (textCol) ro.observe(textCol);
    window.addEventListener("resize", syncHeadshot);

    // Scroll fade-in for sample cards, featured doc, and section eyebrows
    const fadeTargets = root.querySelectorAll(
      ".sample-card, .featured-sample, .reel-embed, .skills-grid, .about-inner, .work-card, .contact-section .contact-inner, .gform-shell"
    );
    fadeTargets.forEach((el) => el.classList.add("reveal-init"));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    fadeTargets.forEach((el) => io.observe(el));

    return () => {
      ro.disconnect();
      io.disconnect();
      window.removeEventListener("resize", syncHeadshot);
    };
  }, [html]);

  // Memoized so React doesn't see a new dangerouslySetInnerHTML object on
  // every parent render. The inner HTML only changes when `html` itself does.
  const innerHtmlProp = useMemo(() => ({ __html: html }), [html]);

  return (
    <div className="App" data-testid="portfolio-root">
      <div ref={containerRef} dangerouslySetInnerHTML={innerHtmlProp} />
    </div>
  );
}

export default App;
